import { useCallback, useEffect, useMemo, useState } from 'react';

import { DesktopPremiumPerks } from '../PremiumBenefits';
import { useHandlePaymentMethod } from '../hooks/useHandlePaymentMethod';

import icStar from '@/assets/ic_star.svg';
import bgDesktop from '@/assets/myAccount/bg_box-premium--account-desktop.jpg';
import bgMobile from '@/assets/myAccount/bg_box-premium--account.jpg';
import { Button, Modal } from '@/components';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useIsLastMatchDay } from '@/hooks/useIsLastMatchDate';
import { TipoProductoTO } from '@/types/myAccount';
import { cn, formatNumberToLocale } from '@/utils/functions';

type Props = {
  isPremium: boolean;
  premiumOffer?: TipoProductoTO['cintillo'] | string;
  txtOffer?: string;
  price: number;
  product?: TipoProductoTO;
  isPromotionUser?: boolean;
  promotionUrl?: string;
};

interface ContentDesktopProps {
  messageMost: JSX.Element;
  isPremium: boolean;
  setIsModalOpen: (value: boolean) => void;
  handlePaymentMethod: () => void;
  isLastMatchday: boolean;
}

const ContentDesktop = ({
  messageMost,
  isPremium,
  handlePaymentMethod,
  setIsModalOpen,
  isLastMatchday,
}: ContentDesktopProps) => {
  return (
    <div className="flex flex-1 flex-col pb-5">
      <div className="w-full text-gdt-gray font-medium text-xs text-left relative">
        {messageMost}
        <ul className="pl-10 space-y-5 pt-8">
          <li className="pl-9">
            <strong className="text-base">
              ¡Disfrutá los beneficios de ser Premium!
            </strong>
          </li>
          <li className="flex gap-3">
            <figure>
              <img src={icStar} width={25} />
            </figure>
            <p>
              <strong>Pines ilimitados</strong> para tus transferencias en tus
              equipos de
              <br />
              Primera y Fútbol 5.
            </p>
          </li>
          <li className="flex gap-3">
            <figure>
              <img src={icStar} width={25} />
            </figure>
            <p>
              Si ganás la fecha en Primera multiplicás tu premio y
              <br />
              <strong>¡te llevás $1.500.000!</strong> Y si sos el campeón
              también multiplicás
              <br />
              tu premio y <strong>¡embolsás $8.500.000!</strong>
            </p>
          </li>
          <li className="flex gap-3">
            <figure>
              <img src={icStar} width={25} />
            </figure>
            <p>
              Jugás automáticamente un Torneo de Amigos exclusivo con
              <br />
              premios para los 10 mejores.
            </p>
          </li>
        </ul>
      </div>
      <div className="flex items-center justify-center w-full gap-11 mt-[25px]">
        <Button
          className="font-bold"
          type="button"
          variant="text"
          onClick={() => setIsModalOpen(true)}
        >
          Ver más beneficios
        </Button>
        <Button
          className="w-48"
          disabled={isPremium || isLastMatchday}
          type="button"
          variant="primary"
          onClick={handlePaymentMethod}
        >
          Quiero ser Premium
        </Button>
      </div>
    </div>
  );
};
export const PremiumCard = ({
  premiumOffer,
  isPremium,
  price,
  product,
  isPromotionUser,
  promotionUrl,
}: Props) => {
  const { isMobile, isDesktop } = useIsDesktop();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { handlePaymentMethod } = useHandlePaymentMethod();
  const [componentIsLoading, setComponentIsLoading] = useState(true);
  const isLastMatchday = useIsLastMatchDay();

  useEffect(() => {
    if (componentIsLoading) {
      setComponentIsLoading(false);
    }
  }, [componentIsLoading]);

  // Handler modificado para la promoción
  const handlePaymentMethodOverride = () => {
    if (isPromotionUser) {
      window.open(promotionUrl, '_blank');
    } else {
      handlePaymentMethod({ product });
    }
  };

  const messageMost = (
    <div className="flex justify-center items-center w-[89px] lg:w-32 px-1.5 py-0.5 rounded-[3px] lg:rounded-[4px] bg-[#4f4f4f] absolute lg:-top-3.5 lg:bottom-auto -bottom-2.5 left-1/2 -translate-x-1/2">
      <p className="text-3xs lg:text-sm font-bold text-white">MÁS ELEGIDO</p>
    </div>
  );

  const titlePrice = (
    <div className="text-left lg:absolute lg:bottom-11 lg:left-8 lg:font-bebas lg:flex items-center lg:bg-[#020102] lg:rounded lg:border lg:border-[#414141] lg:py-4 lg:px-4">
      <p className="text-xs font-bold lg:font-light text-gdt-gray-3 lg:text-[29px] lg:text-white">
        PINES ILIMITADOS
      </p>
      <span className="font-bold lg:font-light text-2xl text-gdt-gray lg:text-[29px] lg:text-white lg:ml-2">
        $ {formatNumberToLocale(price)}
      </span>
    </div>
  );

  const ContentMobile = useCallback(() => {
    if (isMobile) {
      return (
        <div className="flex px-5 h-[87px] justify-between items-center">
          <div className="flex items-center justify-start gap-2">
            <img src={icStar} />
            {titlePrice}
          </div>
          <Button
            className="w-28 h-9 p-0"
            disabled={isPremium || isLastMatchday}
            type="button"
            variant="primary"
            onClick={handlePaymentMethodOverride}
          >
            Obtener
          </Button>
        </div>
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, isPremium]);

  const offer: Props['premiumOffer'] = useMemo(() => {
    if (typeof premiumOffer === 'string') return String(premiumOffer);
    return premiumOffer as Props['premiumOffer'];
  }, [premiumOffer]);

  const offerMessage = useMemo(() => {
    if (!premiumOffer) return;

    const isOfferObject = typeof offer !== 'string';
    const bgColor = isOfferObject ? offer?.colorBackground : undefined;
    const fgColor = isOfferObject ? offer?.colorForeground : undefined;
    const offerText = isOfferObject ? offer?.txtOferta : offer;

    return (
      <figcaption
        className={cn(
          'absolute top-0 left-0 flex justify-center items-center h-[27px] bg-[#ba9c06] w-full rounded-tl-lg rounded-tr-lg lg:rounded-tr-none'
        )}
        style={bgColor ? { backgroundColor: `#${bgColor}` } : undefined}
      >
        <p
          className={cn('text-xs font-bold text-white')}
          style={fgColor ? { color: `#${fgColor}` } : undefined}
        >
          {offerText}
        </p>
      </figcaption>
    );
  }, [offer, premiumOffer]);

  return (
    <>
      <div className=" bg-white text-center lg:mt-7 mt-4 max-w-xl lg:max-w-[985px] w-full mx-auto lg:flex border-gdt-gray-2 rounded-[11px] border-2 lg:h-[314px] relative">
        {isMobile && messageMost}
        <figure className="relative">
          {offerMessage}
          <img
            alt="PACK PREMIUM"
            className={`imgTagResponsive w-full lg:h-[310px] rounded-tl-[9px] lg:rounded-tl-[8px] rounded-tr-[9px] lg:rounded-bl-[7px] lg:rounded-tr-none ${
              !isPremium && !isLastMatchday ? 'cursor-pointer' : ''
            }`}
            decoding="async"
            loading="lazy"
            src={isMobile ? bgMobile : bgDesktop}
            onClick={() =>
              isPremium || isLastMatchday ? null : handlePaymentMethodOverride()
            }
          />
          {isDesktop && titlePrice}
        </figure>
        {isMobile && !isDesktop ? (
          <ContentMobile />
        ) : (
          <ContentDesktop
            handlePaymentMethod={handlePaymentMethodOverride}
            isLastMatchday={isLastMatchday}
            isPremium={isPremium}
            messageMost={messageMost}
            setIsModalOpen={setIsModalOpen}
          />
        )}
        <Modal
          controlledOnClose={() => setIsModalOpen(false)}
          isControlledOpen={isModalOpen}
          render={() => (
            <DesktopPremiumPerks
              isLastMatchDay={isLastMatchday}
              isPremium={isPremium}
              overrideAction={handlePaymentMethodOverride}
              product={product}
            />
          )}
          size="xs"
          title="Disfrutá los beneficios de ser Premium"
          titleClassName="text-lg text-gdt-gray w-full text-center"
          variant="controlled"
        />
      </div>
    </>
  );
};
